/**
 * advance-carrier-squadrons.ts — per-tick carrier squadron lifecycle (v3M17.2).
 *
 * Three responsibilities, run in this order each tick:
 *   1. Rebuild ticking — every `SQUADRON_REBUILD_TICKS_PER_FIGHTER` ticks,
 *      each carrier with `embarkedSquadronCount < CARRIER_SQUADRON_CAPACITY`
 *      accumulates +1 in `rebuildProgressFighters`. On hitting
 *      `SQUADRON_FIGHTER_COUNT`, spawn a docked squadron
 *      (`embarkedSquadronCount += 1`) and reset the accumulator. Halts
 *      while the carrier is mid-jump, EMP-disabled, or destroyed.
 *   2. Auto-recall — for each deployed squadron, check the parent
 *      carrier's `carrierDefenseBehaviour`. With 'return-at-low' the
 *      squadron recalls itself when its `liveFighterCount` drops to
 *      `SQUADRON_DEFENSE_RECALL_THRESHOLD` (= 2). 'no-surrender' never
 *      auto-recalls. Recall mechanics: despawn the squadron, increment
 *      `embarkedSquadronCount` (capped), credit surviving fighters to
 *      `rebuildProgressFighters` with rollover.
 *   3. Auto-launch — for each carrier with `carrierAttackBehaviour !==
 *      'none'`, `embarkedSquadronCount > 0`, and no own squadron
 *      currently deployed, check whether any team-visible enemy is in
 *      the configured range (visionRange / scanRange). If yes, spawn
 *      `embarkedSquadronCount` squadrons in a Vogel-spiral around the
 *      carrier and zero the count.
 *
 * Order is intentional: rebuild first so this tick's freshly-rebuilt
 * squadrons are eligible for launch; recall second so a low-count
 * deployed squadron returns home before launch evaluates "any deployed?";
 * launch third so a freshly-emptied carrier (recalled all squadrons) can
 * re-launch the same tick once an enemy enters range. The "no
 * re-launch while any deployed" gate naturally throttles thrash: the
 * carrier waits for its full wave to come home before sending another.
 *
 * Runs AFTER advance-combat so post-combat `liveFighterCount` decisions
 * are visible, and BEFORE advance-ai so the AI sees the freshly
 * launched/recalled state.
 *
 * v3M17.5 will hook the parent-carrier-destroyed sever path into
 * despawnShip and the carrier-jumps-with-deployed-squadrons auto-recall
 * pre-emption into the three jump appliers.
 */

import { despawnShip } from './advance-combat';
import { initWeaponSlots } from './scenario';
import {
  CARRIER_SQUADRON_CAPACITY,
  SQUADRON_CHASSIS_BY_RACE,
  SQUADRON_CHASSIS_NAMES,
  SQUADRON_DEFENSE_RECALL_THRESHOLD,
  SQUADRON_FIGHTER_COUNT,
  SQUADRON_REBUILD_TICKS_PER_FIGHTER,
  isCarrierTemplate,
} from './balance-carrier-squadrons';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import type {
  Empire,
  GameState,
  RaceId,
  ShipInstance,
  ShipInstanceId,
  ShipTemplate,
} from '../state/types/index';
import { allocEntityId } from '../state/types/index';

/**
 * Run one carrier-squadron tick. Mutates state. Idempotent on absent
 * carriers (no-op for empires / saves without any carrier-chassis ships).
 *
 * MFix13.3e adds the v3M17.0-designed-but-never-shipped tactical AI:
 * deployed squadrons pursue the nearest visible enemy in scan range,
 * drift back to the carrier when no targets remain, and dock on
 * arrival.
 */
export function advanceCarrierSquadrons(state: GameState): void {
  // Snapshot the ship list before we start spawning — auto-launch can
  // add new squadron entries to state.ships and we don't want them
  // included in this tick's recall walk.
  const ships = [...state.ships.values()];

  // (1) Rebuild tick — gated on the global tick % cadence.
  if (state.tick % SQUADRON_REBUILD_TICKS_PER_FIGHTER === 0) {
    for (const ship of ships) {
      if (ship.embarkedSquadronCount === undefined) continue;
      tickRebuild(state, ship);
    }
  }

  // (2) Auto-recall — walk deployed squadrons, dispatch by Defense.
  for (const ship of ships) {
    if (ship.parentCarrierShipId === undefined) continue;
    if (ship.currentHull <= 0) continue;
    autoRecallIfTriggered(state, ship);
  }

  // (3) Auto-launch — walk carriers, dispatch by Attack. Runs BEFORE
  // the tactical / dock passes (MFix13.3e) so freshly-launched
  // squadrons get a tactical moveOrder on the same tick they spawn
  // (otherwise the launch wave sits motionless at Vogel offsets until
  // the next sim tick — observable as a one-tick "freeze" before
  // pursuit kicks in). The auto-launch suppression on already-deployed
  // squadrons still holds because the recall path doesn't despawn
  // until the dock check (post-launch), so a low-count squadron
  // recalled in (2) still blocks re-launch in (3) until it actually
  // docks.
  for (const ship of ships) {
    if (ship.embarkedSquadronCount === undefined) continue;
    if (ship.currentHull <= 0) continue;
    autoLaunchIfTriggered(state, ship);
  }

  // (4) MFix13.3e — squadron tactical AI. Write a moveOrder onto every
  // deployed squadron: pursue the nearest visible enemy in the carrier's
  // scan footprint, or drift home to the carrier when no targets remain.
  // Squadrons marked `isReturning` (via manual recall or auto-recall-at-
  // low) ignore pursuit and head straight to the carrier. Uses a fresh
  // snapshot of `state.ships` so squadrons spawned in (3) are included.
  const shipsAfterLaunch = [...state.ships.values()];
  for (const ship of shipsAfterLaunch) {
    if (ship.parentCarrierShipId === undefined) continue;
    if (ship.currentHull <= 0) continue;
    advanceSquadronTactical(state, ship);
  }

  // (5) MFix13.3e — dock check. Any squadron that has reached its carrier
  // docks silently (graceful despawn, credit liveFighterCount to rebuild
  // progress, increment embarkedSquadronCount). Runs after the tactical
  // step so a squadron whose moveOrder was just cleared by arriving at
  // the carrier this tick gets picked up immediately.
  for (const ship of shipsAfterLaunch) {
    if (ship.parentCarrierShipId === undefined) continue;
    if (ship.currentHull <= 0) continue;
    // Ship may have been despawned earlier in this pass; skip if gone.
    if (!state.ships.has(ship.id)) continue;
    dockIfArrived(state, ship);
  }
}

// ---------------------------------------------------------------------------
// Rebuild
// ---------------------------------------------------------------------------

/**
 * Tick the carrier's `rebuildProgressFighters` by 1. On hitting
 * `SQUADRON_FIGHTER_COUNT`, spawn a docked squadron (increment
 * `embarkedSquadronCount`, capped at `CARRIER_SQUADRON_CAPACITY`) and
 * reset the accumulator. Skips when at-cap, mid-jump, EMP-disabled,
 * or destroyed (the destroyed check is handled in the caller).
 */
function tickRebuild(state: GameState, carrier: ShipInstance): void {
  const docked = carrier.embarkedSquadronCount ?? 0;
  if (docked >= CARRIER_SQUADRON_CAPACITY) return;
  if (carrier.systemId === null) return; // mid-jump
  if (carrier.disabledTicks > 0) return; // EMP

  let progress = (carrier.rebuildProgressFighters ?? 0) + 1;
  if (progress >= SQUADRON_FIGHTER_COUNT) {
    if (docked < CARRIER_SQUADRON_CAPACITY) {
      carrier.embarkedSquadronCount = docked + 1;
      progress -= SQUADRON_FIGHTER_COUNT;
    } else {
      // Cap-discard branch — should be unreachable due to the guard
      // above but keeps the rollover loop closed defensively.
      progress = SQUADRON_FIGHTER_COUNT - 1;
    }
  }
  carrier.rebuildProgressFighters = progress;
  void state; // keep signature consistent with other helpers in this file
}

// ---------------------------------------------------------------------------
// Auto-recall
// ---------------------------------------------------------------------------

/**
 * If the squadron's parent carrier has Defense Behaviour `'return-at-low'`
 * and the squadron's `liveFighterCount` has dropped to
 * `SQUADRON_DEFENSE_RECALL_THRESHOLD` or below, recall it. `'no-surrender'`
 * never auto-recalls — squadron rides until destroyed.
 *
 * MFix13.3e — committed-return: this no longer despawns immediately;
 * the squadron flips `isReturning = true` and flies back to the carrier,
 * docking on arrival via the (4) dock check.
 */
function autoRecallIfTriggered(state: GameState, squadron: ShipInstance): void {
  const carrier = squadron.parentCarrierShipId !== undefined
    ? state.ships.get(squadron.parentCarrierShipId)
    : undefined;
  if (!carrier || carrier.currentHull <= 0) return; // sever path handles
  const defense = carrier.carrierDefenseBehaviour;
  if (defense !== 'return-at-low') return;
  if (squadron.isReturning) return; // already committed
  const live = squadron.liveFighterCount ?? 0;
  if (live > SQUADRON_DEFENSE_RECALL_THRESHOLD) return;
  recallSquadron(state, squadron, carrier);
}

// ---------------------------------------------------------------------------
// MFix13.3e — Squadron tactical AI (pursue + drift-home) + dock check
// ---------------------------------------------------------------------------

/**
 * MFix13.3e — write a `moveOrder` for one deployed squadron based on
 * what's in scan range.
 *
 *   - `isReturning === true` → carrier-bound, regardless of targets.
 *   - Otherwise: pursue nearest enemy in carrier's scan range (across
 *     vision ∪ scan channels). If none, drift home to the carrier;
 *     a fresh enemy entering scan range mid-drift will redirect
 *     pursuit on the next tick.
 *
 * Per Michael's MFix13.3e direction (2026-05-22): launch gate uses the
 * `carrierAttackBehaviour` setting (visual-range vs scan-range), but
 * pursuit always uses scan range — once launched, a squadron will
 * chase anything the carrier can scan even if the carrier itself
 * launches only on visual contact.
 */
function advanceSquadronTactical(
  state: GameState,
  squadron: ShipInstance,
): void {
  if (squadron.parentCarrierShipId === undefined) return; // not a squadron
  if (squadron.systemId === null) return; // mid-jump (defensive — squadrons can't jump)
  if (squadron.disabledTicks > 0) return; // EMP'd, hold position
  const carrier = state.ships.get(squadron.parentCarrierShipId);
  if (!carrier || carrier.currentHull <= 0) return; // sever path handles orphans
  if (carrier.systemId !== squadron.systemId) return; // carrier elsewhere; idle

  if (squadron.isReturning) {
    // Committed return — track the carrier's current position each tick.
    squadron.moveOrder = { targetX: carrier.sx, targetY: carrier.sy };
    return;
  }

  const target = pickPursueTarget(state, squadron, carrier);
  if (target) {
    squadron.moveOrder = { targetX: target.sx, targetY: target.sy };
  } else {
    // Implicit drift-home — no flag set, so re-evaluation next tick can
    // still redirect pursuit if an enemy enters scan range.
    squadron.moveOrder = { targetX: carrier.sx, targetY: carrier.sy };
  }
}

/**
 * MFix13.3e — pursue-target picker. Walks the squadron empire's team-
 * vision map for hostile ships seen via either the vision or scan
 * channel in the carrier's system, within the carrier's `scanRange`
 * (NOT weapon range — `targeting.pickTarget` is the weapon-range
 * picker; this is broader because squadrons must close the gap to
 * weapon range themselves). Nearest-to-squadron wins; deterministic
 * tie-break by ascending ship id.
 *
 * Range is gated by carrier's `scanRange` regardless of
 * `carrierAttackBehaviour`. The Attack Behaviour ('visual-range' vs
 * 'scan-range') governs the *launch* trigger; pursuit, once airborne,
 * always uses the full scan footprint.
 */
function pickPursueTarget(
  state: GameState,
  squadron: ShipInstance,
  carrier: ShipInstance,
): ShipInstance | null {
  if (squadron.systemId === null) return null;
  const empire = state.empires.get(squadron.ownerEmpireId);
  if (!empire) return null;
  const carrierTmpl = state.shipTemplates.get(
    `${empire.raceId}:${carrier.chassis}`,
  );
  if (!carrierTmpl) return null;
  const teamMap = state.teamVision.get(empire.id);
  if (!teamMap) return null;

  const pursueRange = carrierTmpl.scanRange;
  const pursueR2 = pursueRange * pursueRange;

  let best: ShipInstance | null = null;
  let bestD2 = Infinity;
  let bestId = Infinity;
  // Walk in ascending shipId order for determinism on distance ties.
  const ids = [...teamMap.keys()].sort((a, b) => a - b);
  for (const id of ids) {
    const rec = teamMap.get(id);
    if (!rec) continue;
    if (rec.seenVia === null) continue; // ghost record, not currently visible
    if (rec.lastSeenSystemId !== squadron.systemId) continue;
    const enemy = state.ships.get(rec.shipId);
    if (!enemy) continue;
    if (enemy.currentHull <= 0) continue;
    if (enemy.systemId !== squadron.systemId) continue;
    if (enemy.ownerEmpireId === empire.id) continue; // own ship
    // Pursue-range gate: distance from CARRIER (squadrons share the
    // carrier's sensor footprint per design lock).
    const cdx = enemy.sx - carrier.sx;
    const cdy = enemy.sy - carrier.sy;
    if (cdx * cdx + cdy * cdy > pursueR2) continue;
    // Nearest-to-squadron wins (so squadrons in a Vogel-spiral each
    // pick the enemy closest to them, not necessarily the same one).
    const sdx = enemy.sx - squadron.sx;
    const sdy = enemy.sy - squadron.sy;
    const d2 = sdx * sdx + sdy * sdy;
    if (d2 < bestD2 || (d2 === bestD2 && enemy.id < bestId)) {
      best = enemy;
      bestD2 = d2;
      bestId = enemy.id;
    }
  }
  return best;
}

/**
 * MFix13.3e — squadron-arrival-at-carrier dock check. Squadrons that
 * have closed within `formationSpacing` of their carrier dock silently:
 * graceful despawn (no destruction VFX / audio / alert), credit
 * `liveFighterCount` to rebuild progress with rollover, increment
 * `embarkedSquadronCount` capped at `CARRIER_SQUADRON_CAPACITY`.
 *
 * Runs every tick post-tactical-AI. For an `isReturning` squadron the
 * tactical step writes moveOrder = carrier; `advanceShipMovement` may
 * close most of the gap in the same tick (clearing moveOrder on
 * arrival); this check then fires the dock on the same tick or the
 * tick after, depending on relative speeds.
 */
function dockIfArrived(state: GameState, squadron: ShipInstance): void {
  if (squadron.parentCarrierShipId === undefined) return;
  if (squadron.systemId === null) return;
  const carrier = state.ships.get(squadron.parentCarrierShipId);
  if (!carrier || carrier.currentHull <= 0) return;
  if (carrier.systemId !== squadron.systemId) return;

  const dx = squadron.sx - carrier.sx;
  const dy = squadron.sy - carrier.sy;
  const dist2 = dx * dx + dy * dy;
  const dockR = state.balanceData.movement.formationSpacing;
  // MFix13.3e — comparison tolerance (1 part per billion) absorbs FP
  // slack from the Vogel-offset launch path. A squadron launched at
  // `(carrier.sx + spacing, carrier.sy)` re-computes `sx - carrier.sx`
  // as `0.006000000000000005` instead of `0.006` (verified via Node
  // FP repro), so a strict `dist2 > dockR2` rejects a squadron that's
  // exactly at formation-spacing distance. The tolerance is far below
  // any meaningful navigation precision.
  const dockR2 = dockR * dockR * (1 + 1e-9);
  if (dist2 > dockR2) return;

  // Suppress dock on a freshly-launched squadron sitting at its Vogel-
  // spiral offset, unless we're explicitly committed to returning OR
  // there's no pursue target available. Tactical step writes moveOrder
  // each tick, so this gate prevents an instant-dock on the same tick
  // the squadron was launched (squadron and carrier sx/sy may be < dockR
  // apart at the Vogel offsets).
  if (!squadron.isReturning) {
    const pursueTarget = pickPursueTarget(state, squadron, carrier);
    if (pursueTarget) return; // still has work to do
  }

  dockSquadronAtCarrier(state, squadron, carrier);
}

/**
 * MFix13.3e — actually dock a squadron at its carrier. Same crediting
 * math as `recallSquadronInstant`, just without the "instant" framing
 * (this is the natural end of a fly-back trip).
 */
function dockSquadronAtCarrier(
  state: GameState,
  squadron: ShipInstance,
  carrier: ShipInstance,
): void {
  const fighters = squadron.liveFighterCount ?? 0;

  // Graceful — no destruction VFX / audio cue / "Ship lost" alert.
  (state.gracefulDespawnIds ??= new Set()).add(squadron.id);

  despawnShip(state, squadron.id);

  const dockedAfterReturn = Math.min(
    CARRIER_SQUADRON_CAPACITY,
    (carrier.embarkedSquadronCount ?? 0) + 1,
  );
  carrier.embarkedSquadronCount = dockedAfterReturn;

  let progress = (carrier.rebuildProgressFighters ?? 0) + fighters;
  while (progress >= SQUADRON_FIGHTER_COUNT) {
    const current: number = carrier.embarkedSquadronCount ?? 0;
    if (current < CARRIER_SQUADRON_CAPACITY) {
      carrier.embarkedSquadronCount = current + 1;
      progress -= SQUADRON_FIGHTER_COUNT;
    } else {
      progress = SQUADRON_FIGHTER_COUNT - 1;
      break;
    }
  }
  carrier.rebuildProgressFighters = progress;
}

/**
 * Recall a squadron — MFix13.3e commits it to a fly-back-to-carrier
 * trip. Sets `isReturning = true` and writes `moveOrder` toward the
 * carrier's current position; the tactical AI re-targets each tick
 * (carrier may be moving) and the dock check despawns the squadron on
 * arrival. Survivor crediting (`liveFighterCount` → rebuild progress
 * with rollover) and `embarkedSquadronCount` increment happen at
 * arrival in `dockSquadronAtCarrier`, not here.
 *
 * Used by:
 *   - manual `recallCarrierSquadrons` command (commands.ts), and
 *   - Defense Behaviour `'return-at-low'` auto-recall (above).
 *
 * The carrier-jump pre-emption (`recallAllOwnSquadrons`) bypasses this
 * path and goes through `recallSquadronInstant` instead — per Michael's
 * MFix13.3e direction, only carrier jumps should despawn squadrons
 * instantly; everything else should fly back.
 */
export function recallSquadron(
  state: GameState,
  squadron: ShipInstance,
  carrier: ShipInstance,
): void {
  if (squadron.isReturning) return; // already committed; idempotent
  squadron.isReturning = true;
  squadron.moveOrder = { targetX: carrier.sx, targetY: carrier.sy };
  void state; // reserved for future logging / RNG hooks; quiet TS no-unused
}

/**
 * MFix13.3e — instant-despawn recall, used only by the carrier-jump
 * pre-emption path. Mirrors the pre-MFix13.3e `recallSquadron` body:
 * graceful despawn, credit `liveFighterCount` back to rebuild progress
 * with rollover (each rollover spawning another docked squadron up to
 * cap; excess discarded), increment `embarkedSquadronCount` capped at
 * `CARRIER_SQUADRON_CAPACITY`.
 *
 * Distinct from `recallSquadron` (manual + auto-recall-at-low) which
 * flies the squadron home before docking. A jumping carrier can't wait
 * for its squadrons to fly home — physics-of-the-fiction aside, the
 * implementation needs the carrier free to enter charge-up the same
 * tick the jump is committed.
 */
export function recallSquadronInstant(
  state: GameState,
  squadron: ShipInstance,
  carrier: ShipInstance,
): void {
  const fighters = squadron.liveFighterCount ?? 0;

  // MFix13.3b + .3c — graceful despawn: no destruction VFX, no
  // `ship:destroyed` audio cue, no "Ship lost" alert toast.
  (state.gracefulDespawnIds ??= new Set()).add(squadron.id);

  despawnShip(state, squadron.id);

  const dockedAfterReturn = Math.min(
    CARRIER_SQUADRON_CAPACITY,
    (carrier.embarkedSquadronCount ?? 0) + 1,
  );
  carrier.embarkedSquadronCount = dockedAfterReturn;

  let progress = (carrier.rebuildProgressFighters ?? 0) + fighters;
  while (progress >= SQUADRON_FIGHTER_COUNT) {
    // Explicit annotation to break the inference cycle: `current` is
    // initialised from `carrier.embarkedSquadronCount` and the same
    // field is re-assigned inside the loop, so TS without an annotation
    // widens the type to `any`.
    const current: number = carrier.embarkedSquadronCount ?? 0;
    if (current < CARRIER_SQUADRON_CAPACITY) {
      carrier.embarkedSquadronCount = current + 1;
      progress -= SQUADRON_FIGHTER_COUNT;
    } else {
      // At cap — discard the rest of the rebuild credit.
      progress = SQUADRON_FIGHTER_COUNT - 1;
      break;
    }
  }
  carrier.rebuildProgressFighters = progress;
}

// ---------------------------------------------------------------------------
// Auto-launch
// ---------------------------------------------------------------------------

/**
 * Fire a launch on the carrier when Attack Behaviour is satisfied and
 * the carrier holds docked squadrons with no own deployed. Spawns
 * `embarkedSquadronCount` squadrons in a Vogel-spiral around the
 * carrier and zeros the count.
 */
function autoLaunchIfTriggered(state: GameState, carrier: ShipInstance): void {
  const attack = carrier.carrierAttackBehaviour;
  if (attack === undefined || attack === 'none') return;
  if (carrier.systemId === null) return;
  if (carrier.disabledTicks > 0) return;
  if (carrier.cloaked) return;
  const docked = carrier.embarkedSquadronCount ?? 0;
  if (docked === 0) return;
  if (anyOwnSquadronDeployed(state, carrier)) return;

  const empire = state.empires.get(carrier.ownerEmpireId);
  if (!empire) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${carrier.chassis}`);
  if (!tmpl) return;
  if (!isCarrierTemplate(tmpl)) return;

  if (!anyEnemyInLaunchRange(state, carrier, empire, tmpl, attack)) return;

  launchAllDocked(state, carrier, empire);
}

/**
 * True iff at least one squadron in `state.ships` has
 * `parentCarrierShipId === carrier.id`. Prevents re-launch while any
 * of the carrier's own squadrons are still deployed (per design lock —
 * launch cycle ends only when ALL squadrons return).
 */
function anyOwnSquadronDeployed(state: GameState, carrier: ShipInstance): boolean {
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) return true;
  }
  return false;
}

/**
 * Walk team-vision for the carrier's empire. For 'visual-range' check
 * each enemy in the carrier's system seen via the 'vision' channel
 * within the carrier's template `visionRange`. For 'scan-range', check
 * 'scan' channel within `scanRange`. Returns true on first match.
 */
function anyEnemyInLaunchRange(
  state: GameState,
  carrier: ShipInstance,
  empire: Empire,
  tmpl: ShipTemplate,
  attack: 'visual-range' | 'scan-range',
): boolean {
  const range = attack === 'visual-range' ? tmpl.visionRange : tmpl.scanRange;
  const channel = attack === 'visual-range' ? 'vision' : 'scan';
  const teamMap = state.teamVision.get(empire.id);
  if (!teamMap) return false;
  const r2 = range * range;
  for (const rec of teamMap.values()) {
    if (rec.seenVia !== channel) continue;
    if (rec.lastSeenSystemId !== carrier.systemId) continue;
    const enemy = state.ships.get(rec.shipId);
    if (!enemy) continue;
    if (enemy.currentHull <= 0) continue;
    if (enemy.systemId !== carrier.systemId) continue;
    const dx = enemy.sx - carrier.sx;
    const dy = enemy.sy - carrier.sy;
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}

/**
 * Spawn `embarkedSquadronCount` squadrons in a Vogel-spiral around the
 * carrier, then zero the count. Shared between auto-launch and the
 * `launchCarrierSquadrons` command. Each squadron is a fresh
 * ShipInstance with `liveFighterCount: 5`, `parentCarrierShipId: carrier.id`,
 * and weaponSlots derived from the squadron template loadout.
 */
export function launchAllDocked(
  state: GameState,
  carrier: ShipInstance,
  empire: Empire,
): void {
  const docked = carrier.embarkedSquadronCount ?? 0;
  if (docked === 0) return;
  if (carrier.systemId === null) return;

  const squadronChassis = SQUADRON_CHASSIS_BY_RACE[empire.raceId as RaceId];
  const squadronTmpl = state.shipTemplates.get(
    `${empire.raceId}:${squadronChassis}`,
  );
  if (!squadronTmpl) return;

  const spacing = state.balanceData.movement.formationSpacing;
  for (let i = 0; i < docked; i++) {
    const offset = vogelOffset(i, spacing);
    const sx = clamp01(carrier.sx + offset.dx);
    const sy = clamp01(carrier.sy + offset.dy);
    spawnSquadron(state, carrier, empire, squadronTmpl, sx, sy);
  }
  carrier.embarkedSquadronCount = 0;
}

/**
 * Vogel-spiral offset for the i-th squadron at standard formation
 * spacing. Mirrors the formation pattern used by `applyMoveShips` and
 * v3M18 warp-link travel — keeps squadrons from spawning on top of
 * each other.
 */
function vogelOffset(i: number, spacing: number): { dx: number; dy: number } {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const r = spacing * Math.sqrt(i + 1);
  const theta = goldenAngle * i;
  return { dx: Math.cos(theta) * r, dy: Math.sin(theta) * r };
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * Spawn a single squadron at (sx, sy) parented to the given carrier.
 * Allocates a new ShipInstanceId, stamps `liveFighterCount: 5` and
 * `parentCarrierShipId`, and initialises weapon slots from the squadron
 * template. Mirrors the spawn pattern in advance-shipyard's
 * `spawnBuiltShip` but specific to squadrons.
 */
function spawnSquadron(
  state: GameState,
  carrier: ShipInstance,
  empire: Empire,
  squadronTmpl: ShipTemplate,
  sx: number,
  sy: number,
): ShipInstanceId {
  const ship: ShipInstance = {
    id: allocEntityId(state),
    chassis: squadronTmpl.chassis,
    ownerEmpireId: empire.id,
    // Hull/shields drawn from the 5x aggregate template — full strength
    // at spawn. v3M17.3 will wire the variable-count HP pool damage
    // chain so currentHull tracks `liveFighterCount × parent.hull`.
    currentHull: effectiveMaxHull(state, empire, squadronTmpl),
    currentShields: effectiveMaxShields(state, empire, squadronTmpl),
    currentEnergy: squadronTmpl.energyCapacity ?? 0,
    disabledTicks: 0,
    cloaked: false,
    systemId: carrier.systemId,
    sx,
    sy,
    moveOrder: null,
    jumpOriginSystemId: null,
    jumpTargetSystemId: null,
    jumpArrivalAngle: null,
    jumpTargetGateId: null,
    pendingWarpGateLinkId: null,
    jumpTicksRemaining: 0,
    chargeUpTicksRemaining: 0,
    jumpFuelCostPaid: 0,
    jumpDurationTotal: 0,
    lastFiredTick: null,
    lastFiredTargetShipId: null,
    pendingJumpSystemId: null,
    pendingJumpArrivalAngle: null,
    pendingColoniseTargetId: null,
    constructionOrder: null,
    bombardOrder: null,
    weaponSlots: initWeaponSlots(
      squadronTmpl.weaponLoadout,
      state.weapons,
      empire.researchedTechs,
    ),
    liveFighterCount: SQUADRON_FIGHTER_COUNT,
    parentCarrierShipId: carrier.id,
    // v3M22.1 — ship-commands defaults; squadrons are tactical-AI-driven
    // once deployed (auto-engage nearest enemy), never accept player
    // patrol/explore/guard commands.
    commandKind: 'idle',
    waypoints: [],
    guardTargetKind: null,
    guardTargetId: null,
  };
  state.ships.set(ship.id, ship);
  empire.shipIds.push(ship.id);
  return ship.id;
}

/**
 * Recall every squadron whose `parentCarrierShipId === carrier.id` via
 * the fly-back path (MFix13.3e). Used by the manual
 * `recallCarrierSquadrons` command. Each squadron flips `isReturning`
 * and flies back to the carrier; the per-tick dock check despawns each
 * on arrival.
 */
export function recallAllOwnSquadrons(
  state: GameState,
  carrier: ShipInstance,
): void {
  const targets: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) targets.push(ship);
  }
  for (const squadron of targets) recallSquadron(state, squadron, carrier);
}

/**
 * MFix13.3e — instant-recall variant for the carrier-jumps-with-deployed-
 * squadrons pre-emption (v3M17.5). A jumping carrier can't wait for its
 * squadrons to fly home, so this routes through `recallSquadronInstant`
 * (graceful silent despawn + rebuild crediting in one shot).
 */
export function recallAllOwnSquadronsInstant(
  state: GameState,
  carrier: ShipInstance,
): void {
  const targets: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) targets.push(ship);
  }
  for (const squadron of targets) recallSquadronInstant(state, squadron, carrier);
}

/** Used by tests and v3M17.5 sever-path. Squadrons of `carrier`. */
export function findOwnSquadrons(
  state: GameState,
  carrier: ShipInstance,
): ShipInstance[] {
  const out: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) out.push(ship);
  }
  return out;
}

/** Squadron-chassis predicate for downstream filters. */
export function isSquadron(ship: ShipInstance): boolean {
  return SQUADRON_CHASSIS_NAMES.has(ship.chassis);
}
